import { LogService } from 'app/services/logging.service';
import { AppModuleServices } from 'app/utils/app-module-util';
import Social from 'entity/Social';
import {
  Presentation,
  PresentationSettings,
  SocialEventType,
  InteractionMode,
  SocialState,
  Convo,
  ConvoPhase,
  Toast,
} from 'messages/websocket.messages';

let _logService: LogService;

const getLogger = (): LogService => {
  if (!_logService) {
    _logService = AppModuleServices.injector.get<LogService>(LogService);
  }
  return _logService;
};

/**
 * @deprecated The method should not be used
 * Instead a selector should be created
 */
export default class SocialStateHelper {
  public static isUserInvited(state: SocialState, userId: string): boolean {
    if (!this.isEventHappening(state, SocialEventType.PRESENTATION)) {
      return false;
    }
    const presentation = state.socialEvent as Presentation;
    return presentation.requestedPresenterIds.includes(userId);
  }

  public static isUserPresenter(state: SocialState, userId: string): boolean {
    if (!this.isEventHappening(state, SocialEventType.PRESENTATION)) {
      return false;
    }
    const presentation = state.socialEvent as Presentation;
    return userId in presentation.presenters;
  }

  public static isEventHappening(state: SocialState, eventType: SocialEventType, logText?: string): boolean {
    const log = getLogger();
    if (!state || !state.socialEvent) {
      return false;
    }
    const isEventType = state.eventType === eventType && state.socialEvent.eventType === eventType;

    if (logText && !isEventType) {
      log.error('Event check failed', eventType, logText);
    }
    return isEventType;
  }

  public static isAnyEventHappening(state: SocialState): boolean {
    if (state && state.eventType === SocialEventType.NONE) {
      return false;
    }
    return (
      this.isEventHappening(state, SocialEventType.CONVO) ||
      this.isEventHappening(state, SocialEventType.TOAST) ||
      this.isEventHappening(state, SocialEventType.PRESENTATION)
    );
  }

  public static isConvoInBannerPhase(state: SocialState): boolean {
    if (this.isEventHappening(state, SocialEventType.CONVO)) {
      const convo = state.socialEvent as Convo;
      return convo.phase === ConvoPhase.BANNER_PHASE;
    }
    return false;
  }

  public static isToastOrConvoAnnounceActive(state: SocialState): boolean {
    if (this.isEventHappening(state, SocialEventType.TOAST)) {
      const toast = state.socialEvent as Toast;
      return toast.toastIsActive;
    }
    if (this.isEventHappening(state, SocialEventType.CONVO)) {
      const convo = state.socialEvent as Convo;
      return convo.phase === ConvoPhase.ANNOUNCE_PHASE;
    }
    return false;
  }

  public static getEventInfo(state: SocialState): {
    presenterCanHearAudience: boolean;
    allowGroupConversations: boolean;
    presenterIds: string[];
    speakingParticipantIds: string[];
    isPresentation: boolean;
  } {
    const defaultInfo = {
      presenterCanHearAudience: false,
      allowGroupConversations: true,
      presenterIds: [],
      speakingParticipantIds: [],
      isPresentation: false,
    };

    if (!this.isAnyEventHappening(state)) {
      return defaultInfo;
    }
    const isPresentation = this.isEventHappening(state, SocialEventType.PRESENTATION);
    const presenterIds = this.getPresenterIds(state);

    // Return info for TOAST, CONVO
    if (!isPresentation) {
      return {
        ...defaultInfo,
        presenterIds: presenterIds,
        isPresentation: false,
      };
    }

    // Return info for PRESENTATION
    const presentation = state.socialEvent as Presentation;
    const speakingParticipantIds = this.getSpeakingParticipantIds(state);
    const allowGroupConversations = presentation.settings?.allowGroupConversations ?? true;
    const presenterCanHearAudience = presentation.settings?.interactionMode === InteractionMode.OPEN;
    return {
      presenterCanHearAudience,
      allowGroupConversations,
      presenterIds,
      speakingParticipantIds,
      isPresentation: true,
    };
  }

  // Get the ids of any event presenter -- active Toast/Convo or Presentation
  public static getPresenterIds(state: SocialState): string[] {
    if (!this.isAnyEventHappening(state)) {
      return [];
    }

    const { socialEvent } = state;
    switch (socialEvent.eventType) {
      case SocialEventType.PRESENTATION:
        return Object.keys(socialEvent.presenters);

      // There is no presenter for the banner phase of a CONVO
      case SocialEventType.CONVO:
        return socialEvent.phase === ConvoPhase.BANNER_PHASE ? [] : [socialEvent.presenter.userId];

      case SocialEventType.TOAST:
        return socialEvent.toastIsActive === true ? [socialEvent.presenter.userId] : [];

      default:
        return [];
    }
  }

  public static getSpeakingParticipantIds(state: SocialState): string[] {
    if (!this.isEventHappening(state, SocialEventType.PRESENTATION)) {
      return [];
    }
    const presentation = state.socialEvent as Presentation;
    return Object.keys(presentation.speakingParticipants ?? {});
  }

  public static isHost(hosts: string[], userId: string, social?: Social): boolean {
    const alternateHosts = social?.alternateHosts;

    if (!hosts && !alternateHosts) {
      return false;
    }

    const { userId: ownerId } = social ?? {};
    if (userId === ownerId) {
      return true;
    }

    const alternateHostIds = alternateHosts?.map((host) => host.userId);
    const currentHosts = [...(hosts || []), ...(alternateHostIds || [])];
    return currentHosts.includes(userId);
  }

  public static isBannedUser(bannedUsers: string[], userId: string): boolean {
    if (!bannedUsers) {
      return false;
    }
    return bannedUsers.includes(userId);
  }

  public static isWaitingUser(waitingUsers: string[], userId: string): boolean {
    if (!waitingUsers) {
      return false;
    }
    return waitingUsers.includes(userId);
  }

  public static isPushToTalkPresentationHappening(state: SocialState): boolean {
    if (!this.isEventHappening(state, SocialEventType.PRESENTATION)) {
      return false;
    }
    const presentation = state.socialEvent as Presentation;
    const interactionMode = presentation?.settings?.interactionMode;

    return (
      interactionMode === InteractionMode.MANY_PUSH_TO_SPEAK || interactionMode === InteractionMode.SINGLE_PUSH_TO_SPEAK
    );
  }

  public static pushToTalkAvailable({ state, userId }: { state: SocialState; userId: string }): boolean {
    if (!this.isPushToTalkPresentationHappening(state)) {
      return false;
    }
    if (this.isUserPresenter(state, userId)) {
      return false;
    }
    if (this.isSpeakingParticipant({ state, userId })) {
      return true;
    }
    const presentation = state.socialEvent as Presentation;
    const interactionMode = presentation?.settings?.interactionMode;

    if (interactionMode === InteractionMode.SINGLE_PUSH_TO_SPEAK) {
      if (Object.keys(presentation.speakingParticipants ?? {}).length > 0) {
        return false;
      }
    }

    if (interactionMode === InteractionMode.MANY_PUSH_TO_SPEAK) {
      // TODO: find a better way to centralize subscription limit
      if (Object.keys(presentation.speakingParticipants ?? {}).length > 150) {
        return false;
      }
    }
    return true;
  }

  public static getInteractionMode(state: SocialState) {
    const presentation = state.socialEvent as Presentation;
    return presentation?.settings?.interactionMode;
  }

  public static isQueuedQAndAHappening(state: SocialState): boolean {
    if (!this.isEventHappening(state, SocialEventType.PRESENTATION)) {
      return false;
    }
    const presentation = state.socialEvent as Presentation;
    const interactionMode = presentation?.settings?.interactionMode;

    return interactionMode === InteractionMode.QUEUED_Q_AND_A;
  }

  public static isSpeakingParticipant({ state, userId }: { state: SocialState; userId: string }): boolean {
    if (!this.isEventHappening(state, SocialEventType.PRESENTATION)) {
      return false;
    }
    const presentation = state.socialEvent as Presentation;
    return !!presentation.speakingParticipants?.[userId];
  }

  public static isSpeakerOrPresenter({ state, userId }: { state: SocialState; userId: string }): boolean {
    const isSpeakingParticipant = SocialStateHelper.isSpeakingParticipant({ state, userId });
    const isPresenter = SocialStateHelper.isUserPresenter(state, userId);

    return isSpeakingParticipant || isPresenter;
  }

  public static getPresentationSettings(state: SocialState): PresentationSettings | undefined {
    if (!this.isEventHappening(state, SocialEventType.PRESENTATION)) {
      return undefined;
    }
    const presentation = state.socialEvent as Presentation;

    return presentation.settings;
  }

  public static getNewPresentationSettingsState({
    state,
    updatedProperties,
  }: {
    state: SocialState;
    updatedProperties: Partial<PresentationSettings>;
  }): PresentationSettings {
    if (!state.socialEvent || state.socialEvent.eventType !== SocialEventType.PRESENTATION) {
      throw new Error('Cannot update settings for no presentation');
    }
    const { settings } = state.socialEvent as Presentation;
    const propertiesToUpdate = { ...updatedProperties };

    const newPresentationSettings: PresentationSettings = {
      defaultFullScreen: false,
      allowGroupConversations: true,
      interactionMode: InteractionMode.MANY_PUSH_TO_SPEAK,
      requireFullScreen: false,
      ...settings,
      ...propertiesToUpdate,
    };

    return newPresentationSettings;
  }

  public static getNewPresentationState({
    state,
    updatedProperties,
  }: {
    state: SocialState;
    updatedProperties: Partial<Presentation>;
  }): Presentation {
    if (!this.isEventHappening(state, SocialEventType.PRESENTATION)) {
      throw new Error('Cannot update presentation properties without a presentation');
    }
    const presentation = state.socialEvent as Presentation;
    const propertiesToUpdate = { ...updatedProperties };

    const newPresentation: Presentation = {
      ...presentation,
      ...propertiesToUpdate,
    };

    return newPresentation;
  }

  public static interactionModeIs(state: SocialState, interactionMode: InteractionMode): boolean {
    if (!this.isEventHappening(state, SocialEventType.PRESENTATION)) {
      return false;
    }
    const socialEvent = state.socialEvent as Presentation;
    const currentMode = socialEvent.settings?.interactionMode;
    return currentMode === interactionMode;
  }

  public static isQueuedToSpeak({ state, userId }: { state: SocialState; userId: string }): boolean {
    if (!this.isEventHappening(state, SocialEventType.PRESENTATION)) {
      return false;
    }
    const socialEvent = state.socialEvent as Presentation;
    return !!socialEvent.queuedToSpeakIds?.includes(userId);
  }

  public static isDefaultFullscreenActive(state: SocialState): boolean {
    if (!this.isEventHappening(state, SocialEventType.PRESENTATION)) {
      return false;
    }
    const socialEvent = state.socialEvent as Presentation;
    return !!socialEvent.settings?.defaultFullScreen;
  }

  public static isRequireFullscreenActive(state: SocialState): boolean {
    if (!this.isEventHappening(state, SocialEventType.PRESENTATION)) {
      return false;
    }
    const socialEvent = state.socialEvent as Presentation;
    return !!socialEvent.settings?.requireFullScreen;
  }

  public static hasFullScreenBeenTriggered(state: SocialState, prevState: SocialState): boolean {
    return !!(
      this.getPresentationSettings(state)?.defaultFullScreen &&
      !this.getPresentationSettings(prevState)?.defaultFullScreen
    );
  }

  public static getQueuedQAndADepth(state: SocialState): number {
    if (!this.isQueuedQAndAHappening(state)) {
      return 0;
    }
    const socialEvent = state.socialEvent as Presentation;
    return socialEvent.queuedToSpeakIds?.length ?? 0;
  }

  public static getPresentersNames(state: SocialState): string {
    if (!this.isAnyEventHappening(state)) {
      return '';
    }

    const { socialEvent } = state;
    switch (socialEvent.eventType) {
      case SocialEventType.PRESENTATION:
        if (Object.values(socialEvent.presenters).length > 0) {
          return Object.values(socialEvent.presenters)
            .map((p) => `${p.firstName}${p.lastName ? ' ' : ''}${p.lastName || ''}`)
            .join(', ');
        } else {
          return 'Presentation';
        }

      // There is no presenter for the banner phase of a CONVO
      case SocialEventType.CONVO:
        return socialEvent.phase === ConvoPhase.BANNER_PHASE
          ? ''
          : `${socialEvent.presenter.firstName} ${socialEvent.presenter.lastName || ''}`;

      case SocialEventType.TOAST:
        return `${socialEvent.presenter.firstName} ${socialEvent.presenter.lastName || ''}`;

      default:
        return '';
    }
  }
}
